package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.PercentFormatter
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraAnalytics
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraDirectorate
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraDirectorateUnit
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraReasonCount
import br.com.vvrefeicoes.portal.feature.operacional.DonutChart
import br.com.vvrefeicoes.portal.feature.operacional.DonutSlice
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.ChevronUp
import com.composables.icons.lucide.Ellipsis
import com.composables.icons.lucide.Lucide
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_orders
import portalvv.shared.generated.resources.compras_panel_extras_by_directorate
import portalvv.shared.generated.resources.compras_panel_extras_directorate
import portalvv.shared.generated.resources.compras_panel_extras_legend
import portalvv.shared.generated.resources.compras_panel_extras_no_data
import portalvv.shared.generated.resources.compras_panel_extras_no_directorate
import portalvv.shared.generated.resources.compras_panel_extras_reasons
import portalvv.shared.generated.resources.compras_panel_extras_share_column
import portalvv.shared.generated.resources.compras_panel_extras_total_column

private val DIRECTORATE_COLORS =
    listOf(
        AppPalette.Primary500,
        AppPalette.Accent500,
        AppPalette.Info500,
        AppPalette.Warning500,
        AppPalette.Success500,
        AppPalette.Error500,
        AppPalette.Primary900,
        AppPalette.Accent900,
        AppPalette.Neutral500,
    )

private val DIRECTORATE_SIDE_BY_SIDE = 1000.dp
private val DIRECTORATE_TABLE_MIN_WIDTH = 520.dp
private const val EXTRA_DIRECTORATE_WEIGHT = 1.8f
private const val EXTRA_MONTH_WEIGHT = 0.8f
private const val EXTRA_TOTAL_WEIGHT = 0.9f
private const val EXTRA_SHARE_WEIGHT = 0.8f

@Composable
internal fun ExtrasDirectorateSection(
    analytics: PurchasesExtraAnalytics,
    months: List<MonthPeriod> = analytics.months,
) {
    val directorates = analytics.directorates
    val total = directorates.sumOf { it.orderCount }
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text = stringResource(Res.string.compras_panel_extras_by_directorate),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        if (directorates.isEmpty()) {
            Text(
                text = stringResource(Res.string.compras_panel_extras_no_data),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            return@Column
        }
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val availableForTable = (maxWidth - AppSpacing.space16) * (1.25f / 2.25f)
            val sideBySide =
                maxWidth >= DIRECTORATE_SIDE_BY_SIDE &&
                    availableForTable >= directorateTableMinWidth(months)
            if (sideBySide) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                    verticalAlignment = Alignment.Top,
                ) {
                    ExtrasDirectorateTable(
                        directorates = directorates,
                        months = months,
                        total = total,
                        modifier = Modifier.weight(1.25f),
                    )
                    ExtrasDirectorateDonut(
                        directorates = directorates,
                        modifier = Modifier.weight(1f),
                    )
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                    ExtrasDirectorateTable(
                        directorates = directorates,
                        months = months,
                        total = total,
                    )
                    ExtrasDirectorateDonut(directorates = directorates)
                }
            }
        }
    }
}

@Composable
private fun ExtrasDirectorateTable(
    directorates: List<PurchasesExtraDirectorate>,
    months: List<MonthPeriod>,
    total: Long,
    modifier: Modifier = Modifier,
) {
    val multiMonth = months.size > 1
    BoxWithConstraints(modifier = modifier) {
        val minWidth = directorateTableMinWidth(months)
        val tableWidth = if (maxWidth < minWidth) minWidth else maxWidth
        val scrollState = rememberScrollState()
        Box(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState).clipToBounds()) {
            Surface(
                modifier = Modifier.width(tableWidth),
                shape = AppShapes.medium,
                color = MaterialTheme.colorScheme.surface,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    ExtrasDirectorateHeader(months = months, multiMonth = multiMonth)
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    directorates.forEachIndexed { index, directorate ->
                        ExtrasDirectorateRow(
                            directorate = directorate,
                            months = months,
                            multiMonth = multiMonth,
                            total = total,
                        )
                        if (index < directorates.lastIndex) {
                            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ExtrasDirectorateHeader(
    months: List<MonthPeriod>,
    multiMonth: Boolean,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(AppPalette.Neutral50)
                .padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        ExtrasHeaderCell(
            text = stringResource(Res.string.compras_panel_extras_directorate),
            weight = EXTRA_DIRECTORATE_WEIGHT,
            align = TextAlign.Start,
        )
        if (multiMonth) {
            months.forEach { month ->
                ExtrasHeaderCell(
                    text = monthLabel(month),
                    weight = EXTRA_MONTH_WEIGHT,
                )
            }
        }
        ExtrasHeaderCell(
            text = stringResource(Res.string.compras_panel_extras_total_column),
            weight = EXTRA_TOTAL_WEIGHT,
        )
        ExtrasHeaderCell(
            text = stringResource(Res.string.compras_panel_extras_share_column),
            weight = EXTRA_SHARE_WEIGHT,
        )
    }
}

@Composable
private fun RowScope.ExtrasHeaderCell(
    text: String,
    weight: Float,
    align: TextAlign = TextAlign.End,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = align,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun ExtrasDirectorateRow(
    directorate: PurchasesExtraDirectorate,
    months: List<MonthPeriod>,
    multiMonth: Boolean,
    total: Long,
) {
    var expanded by remember { mutableStateOf(false) }
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .clickable { expanded = !expanded }
                    .padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                imageVector = if (expanded) Lucide.ChevronUp else Lucide.ChevronDown,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(16.dp),
            )
            Text(
                text = directorate.name ?: stringResource(Res.string.compras_panel_extras_no_directorate),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(EXTRA_DIRECTORATE_WEIGHT).padding(start = AppSpacing.space8),
            )
            if (multiMonth) {
                months.forEach { month ->
                    val count =
                        directorate.months
                            .firstOrNull { it.year == month.year && it.month == month.month }
                            ?.orderCount
                            ?: 0L
                    ExtrasValueCell(text = count.toString(), weight = EXTRA_MONTH_WEIGHT)
                }
            }
            ExtrasValueCell(text = directorate.orderCount.toString(), weight = EXTRA_TOTAL_WEIGHT)
            ExtrasValueCell(
                text = "${PercentFormatter.format(share(directorate.orderCount, total))}%",
                weight = EXTRA_SHARE_WEIGHT,
            )
        }
        AnimatedVisibility(visible = expanded, enter = expandVertically(), exit = shrinkVertically()) {
            Column(modifier = Modifier.fillMaxWidth()) {
                directorate.units.forEach { unit ->
                    ExtrasDirectorateUnitRow(unit = unit, months = months, multiMonth = multiMonth)
                }
            }
        }
    }
}

@Composable
private fun ExtrasDirectorateUnitRow(
    unit: PurchasesExtraDirectorateUnit,
    months: List<MonthPeriod>,
    multiMonth: Boolean,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space4),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Spacer(modifier = Modifier.size(16.dp))
        Text(
            text = unit.name,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(EXTRA_DIRECTORATE_WEIGHT).padding(start = AppSpacing.space8),
        )
        if (multiMonth) {
            months.forEach { month ->
                val count =
                    unit.months
                        .firstOrNull { it.year == month.year && it.month == month.month }
                        ?.orderCount
                        ?: 0L
                ExtrasValueCell(
                    text = if (count > 0L) count.toString() else NO_TABLE_VALUE,
                    weight = EXTRA_MONTH_WEIGHT,
                )
            }
        }
        ExtrasValueCell(text = unit.orderCount.toString(), weight = EXTRA_TOTAL_WEIGHT)
        Spacer(modifier = Modifier.weight(EXTRA_SHARE_WEIGHT))
    }
}

@Composable
private fun RowScope.ExtrasValueCell(
    text: String,
    weight: Float,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurface,
        textAlign = TextAlign.End,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun ExtrasDirectorateDonut(
    directorates: List<PurchasesExtraDirectorate>,
    modifier: Modifier = Modifier,
) {
    val total = directorates.sumOf { it.orderCount }
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text = stringResource(Res.string.compras_orders, total.toInt()),
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
            modifier = Modifier.align(Alignment.CenterHorizontally),
        )
        DonutChart(
            slices =
                directorates.mapIndexed { index, directorate ->
                    DonutSlice(
                        label = directorate.name ?: stringResource(Res.string.compras_panel_extras_no_directorate),
                        value = directorate.orderCount,
                        color = DIRECTORATE_COLORS[index % DIRECTORATE_COLORS.size],
                    )
                },
            modifier = Modifier.align(Alignment.CenterHorizontally).size(220.dp),
            formatValue = { "$it pedidos" },
        )
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            directorates.forEachIndexed { index, directorate ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(
                        modifier =
                            Modifier
                                .size(12.dp)
                                .background(
                                    DIRECTORATE_COLORS[index % DIRECTORATE_COLORS.size],
                                    AppShapes.small,
                                ),
                    )
                    Text(
                        text =
                            stringResource(
                                Res.string.compras_panel_extras_legend,
                                directorate.name
                                    ?: stringResource(Res.string.compras_panel_extras_no_directorate),
                                directorate.orderCount.toInt(),
                            ),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurface,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
    }
}

@Composable
internal fun ExtrasReasonsSection(reasons: List<PurchasesExtraReasonCount>) {
    val total = reasons.sumOf { it.orderCount }
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text = stringResource(Res.string.compras_panel_extras_reasons),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        BoxWithConstraints {
            if (maxWidth >= 900.dp) {
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    reasons.chunked(3).forEach { rowReasons ->
                        Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                            rowReasons.forEach { reason ->
                                ReasonCard(reason = reason, total = total, modifier = Modifier.weight(1f))
                            }
                            repeat(3 - rowReasons.size) {
                                Spacer(modifier = Modifier.weight(1f))
                            }
                        }
                    }
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    reasons.forEach { reason ->
                        ReasonCard(reason = reason, total = total, modifier = Modifier.fillMaxWidth())
                    }
                }
            }
        }
    }
}

@Composable
private fun ReasonCard(
    reason: PurchasesExtraReasonCount,
    total: Long,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.heightIn(min = 96.dp),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.padding(AppSpacing.space16),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Surface(
                modifier = Modifier.size(44.dp),
                shape = AppShapes.medium,
                color = AppPalette.Purple100,
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = Lucide.Ellipsis,
                        contentDescription = null,
                        tint = AppPalette.Purple900,
                        modifier = Modifier.size(22.dp),
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = reason.name,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = "${reason.orderCount} (${PercentFormatter.format(share(reason.orderCount, total))} %)",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface,
                )
            }
        }
    }
}

private fun directorateTableMinWidth(months: List<MonthPeriod>) =
    DIRECTORATE_TABLE_MIN_WIDTH +
        if (months.size > 1) {
            (months.size * 72).dp
        } else {
            0.dp
        }

private fun share(
    value: Long,
    total: Long,
): Double =
    if (total <= 0L) {
        0.0
    } else {
        value.toDouble() / total * 100.0
    }

private fun monthLabel(month: MonthPeriod): String {
    val names =
        listOf("Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez")
    return "${names.getOrElse(month.month - 1) { month.month.toString() }}/${month.year}"
}
